import PersonalAccountBeneficiaryApiClient from "@/api/personalAccountBeneficiaryApiClient";
import { AppToasterV2, dialogStore } from "@ucl/library";
import { useEffect, useState } from "react";
import { useAuthenticatedApiClient } from "./useAuthenticatedApiClient";
import { useFinancialApplicationForm } from "./useFinancialApplicationForm";

export const usePersonalAccountBeneficiaries = (financialApplicationId: string) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [personalAccountBeneficiaryIds, setPersonalAccountBeneficiaryIds] = useState<string[]>([]);

  const personalAccountBeneficiaryApiClient = useAuthenticatedApiClient(
    PersonalAccountBeneficiaryApiClient
  );

  const { refreshFinancialApplication } = useFinancialApplicationForm(financialApplicationId);

  useEffect(() => {
    getPersonalAccountBeneficiaryIds();
  }, [financialApplicationId]);

  const getPersonalAccountBeneficiaryIds = async () => {
    setIsLoading(true);
    personalAccountBeneficiaryApiClient
      .getPersonalAccountBeneficiaryIds(financialApplicationId)
      .then((ids) => {
        setPersonalAccountBeneficiaryIds(ids);
      })
      .catch((error) => {
        console.error(error);
        AppToasterV2.showFailedActionToaster("Failed to get beneficiary IDs.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDeletePersonalAccountBeneficiary = (personalAccountBeneficiaryId: string) => {
    dialogStore.openConfirmDialog({
      dialogId: "delete-personal-account-beneficiary-" + personalAccountBeneficiaryId,
      headerText: "Delete Beneficiary",
      content: "Are you sure you want to delete this beneficiary?",
      confirmButtonText: "Delete",
      loadingProgressContent: "Delete",
      loadingSuccessContent: "Delete",
      color: "sunset-red",
      onConfirm: async () => {
        const result = {
          isSuccess: true,
          error: undefined,
        };

        try {
          await personalAccountBeneficiaryApiClient.deletePersonalAccountBeneficiary(
            financialApplicationId,
            personalAccountBeneficiaryId
          );
          setPersonalAccountBeneficiaryIds(
            personalAccountBeneficiaryIds.filter((id) => id !== personalAccountBeneficiaryId)
          );
          dialogStore.closeDialog(
            "delete-personal-account-beneficiary-" + personalAccountBeneficiaryId
          );
          AppToasterV2.showSuccessAppToaster("Beneficiary deleted.");
          await refreshFinancialApplication();
        } catch (error: any) {
          console.error(error); 
          result.isSuccess = false;
          result.error = error;
        }

        return result;
      },
    });
  };

  const handleAddPersonalAccountBeneficiary = async () => {
    try {
      setIsCreating(true);
      const beneficiary =
        await personalAccountBeneficiaryApiClient.createPersonalAccountBeneficiary(
          financialApplicationId
        );
      if (beneficiary.id) {
        setPersonalAccountBeneficiaryIds([beneficiary.id, ...personalAccountBeneficiaryIds]);
        AppToasterV2.showSuccessAppToaster("Beneficiary added.");
        await refreshFinancialApplication();
      }
    } catch (error: any) {
      console.error(error);
      AppToasterV2.showFailedActionToaster("Failed to add beneficiary.");
    } finally {
      setIsCreating(false);
    }
  };

  return {
    personalAccountBeneficiaryIds,
    handleDeletePersonalAccountBeneficiary,
    handleAddPersonalAccountBeneficiary,
    isLoading,
    isCreating,
  };
};
